/*
 * @Author: edvardchen
 * @Date:   2016-11-22 10:05:12
 * @Last Modified by: zloongjiang
 * @Last Modified time: 2021-07-12 11:14:28
 */

// @flow
import * as React from 'react';
import { visible, listenToScroll } from '../utility';

/**
 * 监听模块露出
 * @param  {React.Component} Component
 * @return {React.Component}
 */
export default function withExposureWatch<T>(
  Component: React.ComponentType<T>,
): React.ComponentType<
  T & {
    onExposure: Function,
    once?: boolean,
    onExposureExtra?: Function,
    extraInterval?: number,
    extraRate?: number,
    visibleFn?: Function,
  },
> {
  class ContainerWithExposure extends React.Component<
    T & {
      onExposure: Function,
      once?: boolean,
      onExposureExtra?: Function,
      extraInterval?: number,
      extraRate?: number,
      visibleFn?: Function,
    },
  > {
    el: ?HTMLDivElement;
    extra: ?boolean;
    extraTimer: ?*;
    times: ?number;
    extraTimes: number;
    watchExposure: ?{
      remove: Function,
    };

    static displayName = `${Component.displayName || Component.name}WithExposure`;

    static defaultProps = {
      once: true,
      extra: false,
      extraInterval: 1000, // 1000ms
      extraRate: 0.5, // 曝光50%以上
    };

    componentDidMount() {
      const { once, onExposure = () => {}, onExposureExtra } = this.props;
      const { el } = this;
      if (!el) {
        return;
      }
      this.extra = typeof onExposureExtra === 'function' || false;
      this.extraTimer = 0;
      this.times = 0;
      this.extraTimes = 0;
      const DELAY = this.extra ? 100 : 300;
      // el = el.childNodes[0]; // 取内容节点
      const visibleFn = this.props.visibleFn || visible;
      if (visibleFn(el)) {
        // $FlowFixMe
        onExposure(el);
        if (once && !this.extra) {
          return;
        }
        if (once) {
          this.times += 1;
        }
        this.exposureExtraHandler(el, null);
      }
      const watch = listenToScroll(() => {
        if (visibleFn(el)) {
          if (this.extra) {
            this.exposureExtraHandler(el, watch);
          }
          // $FlowFixMe
          if ((once && this.times === 0) || !once) onExposure(el);
          this.times += 1;
          if (once && !this.extra) {
            watch.remove();
          }
        }
      }, DELAY);
      this.watchExposure = watch;
    }

    componentWillUnmount() {
      this.watchExposure && this.watchExposure.remove();
    }
    triggerFn = () => {
      const { el } = this;
      if (!el) {
        return;
      }
      const { once, onExposure = () => {} } = this.props;
      const visibleFn = this.props.visibleFn || visible;
      if (visibleFn(el)) {
        if (this.extra) {
          this.exposureExtraHandler(el);
        }
        // $FlowFixMe
        if ((once && this.times === 0) || !once) onExposure(el);
        this.times += 1;
      }
    };
    exposureExtraHandler = (el, watch) => {
      const { extraRate = 0.5, extraInterval = 1000, onExposureExtra = () => {} } = this.props;
      const winH = window.innerHeight;
      const eleH = el.clientHeight;
      const { top, bottom } = el.getBoundingClientRect() || {};
      const rate = bottom > winH ? (winH - top) / eleH : (eleH + top) / eleH;
      if (rate >= extraRate && !this.extraTimer) {
        this.extraTimer = setTimeout(() => {
          const visibleFn = this.props.visibleFn || visible;
          if (visibleFn(el)) {
            // $FlowFixMe
            onExposureExtra(el);
            watch && watch.remove();
            this.extraTimes += 1;
          } else {
            this.extraTimer = 0;
          }
          this.extraTimer && clearTimeout(this.extraTimer);
        }, extraInterval);
      } else if (rate < extraRate) {
        this.extraTimer && clearTimeout(this.extraTimer);
        this.extraTimer = 0;
      }
      if (this.extraTimes > 0 && watch) watch.remove(); // 目前露出x%停留y秒的曝光只支持一次，后续有需要拓展
    };
    render() {
      const { onExposure, once, onExposureExtra, wrapSpan = false, cls = '', ...rest } = this.props;
      if (wrapSpan) {
        // 视频标签不用折行
        return (
          <span
            ref={(node) => {
              this.el = node;
            }}
          >
            <Component {...rest} />
          </span>
        );
      }
      return (
        <div
          className={cls}
          ref={(node) => {
            this.el = node;
          }}
        >
          <Component {...rest} />
        </div>
      );
    }
  }

  return ContainerWithExposure;
}
